import React, { useState, useCallback, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import api, { cookieUtils } from '../API/ApiService';
import toast from 'react-hot-toast';
import LoaderOverlay from '../Loader/LoaderOverlay';
import KycLandingPage from './KycLandingPage';
import { useAuth } from '../Auth/useAuth';

const SignerForm = () => {
    const { requestId } = useParams();
    const [loading, setLoading] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [docUrl, setDocUrl] = useState('');
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        idNumber: ''
    });
    const { checkToken } = useAuth();

    useEffect(() => {
      checkToken();
    }, [checkToken]);
    
    const handleProceed = (url) => {
        window.location.href = url;
    };

    const handleInputChange = useCallback((field) => (e) => {
        setFormData(prev => ({
            ...prev,
            [field]: e.target.value
        }));
    }, []);

    const resetForm = useCallback(() => {
        setFormData({
            name: '',
            email: '',
            idNumber: ''
        });
    }, []);

    const handleSubmit = useCallback(async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const sdkToken = cookieUtils.get('sdkToken');
            if (!sdkToken) {
                setLoading(false);
                toast.error('App Keys expired');
                return;
            }
            const response = await api.createESignRequests(
                formData?.name, 
                formData?.email, 
                formData?.idNumber, 
                requestId
            );

            if (response.status) {
                localStorage.setItem('requestInit', 'mock-request');
                toast.success('Initiating mock KYC request');
                setTimeout(()=> {
                    window.location.href = response?.data?.participants[0]?.signUrl
                }, 1000);
                setDocUrl(response?.data?.participants[0]?.signUrl);
                setShowSuccess(false); // set to true to show landing page and remove location.href
                setLoading(false);

            }
        } catch (error) {
            console.error('Error creating e-sign request:', error);
            toast.error('Failed to create request. Please try again.');
            setLoading(false);
            resetForm();
        }
    }, [formData, requestId, resetForm]);

    if (loading) {
        return <LoaderOverlay />;
    }

    if (showSuccess) {
        return (
            <div className="min-h-screen flex flex-col bg-gray-50">
                <div className="flex-1 flex flex-col py-8 px-4 sm:px-6 lg:px-8">
                    <div className="sm:mx-auto sm:w-full sm:max-w-md">
                        <KycLandingPage onProceed={handleProceed} docUrl={docUrl} />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex flex-col bg-gray-50">
            <div className="flex-1 flex flex-col py-6 px-4 sm:px-6 lg:px-8"> 
                <div className="sm:mx-auto sm:w-full sm:max-w-7xl mb-6"> 
                    <h1 className="text-2xl font-bold text-center text-gray-900 mb-3">
                        Welcome to The world’s first tech that merges AI and biometrics in digital transactions.
                    </h1>
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 shadow-sm mx-2">
                        <div className="flex flex-col gap-2">
                            <h2 className="text-lg font-semibold text-blue-800">
                                Introducing BioConsent
                            </h2>
                            <div className="text-gray-700 text-sm">
                                <p>Recreating a face-to-face transaction in the digital ecosystem, our BioConsent engine uses live, real-time facial recognition and biometrics to tell exactly WHO did WHAT, WHERE, WHEN, and HOW — while defending against fraudsters, 
                                AI, bots, and synthetic identity theft.</p>
                                
                                {/* <div className="mt-1">
                                    <p><b>How it works: </b>
                                    During account sign-ups, access, or any sensitive action vulnerable to identity fraud or AI threats, faceEsign BioConsent runs a fast biometric check — verifying the user is real (not AI), present, and authorized.
                                    It takes seconds and fits seamlessly into your existing flow — no disruption, no replacements, no complicated integrations.</p>
                                </div> */}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="max-w-xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                        <div className="bg-blue-600 px-6 py-8 text-center">
                            <h2 className="text-3xl font-extrabold text-white uppercase">
                                KYC Details
                            </h2>
                            <p className="mt-2 text-blue-100 uppercase">
                                please fill out form below to start the flow
                            </p>
                        </div>

                        <div className="px-6 py-8">
                            <form className="space-y-6" onSubmit={handleSubmit}>
                                <div>
                                    <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                                        Full Name
                                    </label>
                                    <div className="mt-1">
                                        <input
                                            id="name"
                                            name="name"
                                            type="text"
                                            required
                                            className="appearance-none block w-full px-3 py-2 border border-gray-300 
                                            rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            value={formData.name}
                                            onChange={handleInputChange('name')}
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                                        Email Address
                                    </label>
                                    <div className="mt-1">
                                        <input
                                            id="email"
                                            name="email"
                                            type="email"
                                            required
                                            className="appearance-none block w-full px-3 py-2 border border-gray-300 
                                            rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            value={formData.email}
                                            onChange={handleInputChange('email')}
                                        />
                                    </div>
                                </div>

                                {/* <div>
                                    <label htmlFor="idNumber" className="block text-sm font-medium text-gray-700">
                                        ID Number
                                    </label>
                                    <div className="mt-1">
                                        <input
                                            id="idNumber"
                                            name="idNumber"
                                            type="text"
                                            required
                                            className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            value={formData.idNumber}
                                            onChange={handleInputChange('idNumber')}
                                        />
                                    </div>
                                </div> */}

                                <div>
                                    <button
                                        type="submit"
                                        className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
                                    >
                                        Submit
                                    </button>
                                </div>
                            </form>
                        </div>

                        <div className="px-6 py-4 bg-gray-50 text-center text-sm text-gray-600">
                            *You can enter mock details to initiate the process
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SignerForm;